/**
 * BuildPanel — trigger a build for the app's latest version, then poll status
 * until terminal and surface the AAB/APK download links.
 *
 * Status flow comes from BUILD_STATUS in the shared contract:
 *   queued → claimed → building → uploading → succeeded | failed | canceled
 *
 * MVP uses short-polling of GET /v1/builds/{id}. The SSE endpoint
 * (buildsApi.eventsUrl) is available for the open tab as a future enhancement;
 * the authoritative completion signal is the FCM push (mobile/web push), so
 * polling is a fully sufficient fallback for the dashboard.
 */
import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AppVersionDto, BuildDto, SigningMode, SigningStatusDto } from '@appbuilder/shared';
import { LIVE_BUILD_STATUSES } from '@appbuilder/shared';
import { appsApi, buildsApi, signingApi, ApiClientError } from '../api/client';
import { Banner, Button, Card, Field, StatusBadge } from '../components/ui';
import type { BuildStatus } from '@appbuilder/shared';

const POLL_INTERVAL_MS = 3000;

function isLive(status: BuildDto['status']): boolean {
  return (LIVE_BUILD_STATUSES as readonly string[]).includes(status);
}

export function BuildPanelPage(): JSX.Element {
  const { appId = '' } = useParams();

  const [latestVersion, setLatestVersion] = useState<AppVersionDto | null>(null);
  const [build, setBuild] = useState<BuildDto | null>(null);
  const [history, setHistory] = useState<BuildDto[]>([]);
  const [signing, setSigning] = useState<SigningStatusDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load the latest version (to build) + recent build history + signing status.
  useEffect(() => {
    let cancelled = false;
    Promise.all([appsApi.listVersions(appId), buildsApi.history(appId), signingApi.get(appId)])
      .then(([versions, builds, sign]) => {
        if (cancelled) return;
        setLatestVersion(versions[0] ?? null);
        setHistory(builds);
        setSigning(sign);
        // Show the current build's download buttons immediately when Studio opens this page.
        const live = builds.find((b) => isLive(b.status));
        const requestedId = new URLSearchParams(window.location.search).get('buildId');
        setBuild(builds.find((b) => b.id === requestedId) ?? live ?? builds.find((b) => b.status === 'succeeded') ?? builds[0] ?? null);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof ApiClientError ? err.message : 'Failed to load build data');
      });
    return () => {
      cancelled = true;
    };
  }, [appId]);

  // Poll the active build until terminal.
  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!build || !isLive(build.status)) {
      stopPolling();
      return;
    }
    const buildId = build.id;
    pollRef.current = setInterval(async () => {
      try {
        const next = await buildsApi.get(buildId);
        setBuild(next);
        if (!isLive(next.status)) {
          stopPolling();
          // Refresh history so the terminal row is reflected.
          buildsApi.history(appId).then(setHistory).catch(() => {});
        }
      } catch (err) {
        if (err instanceof ApiClientError) setError(err.message);
      }
    }, POLL_INTERVAL_MS);

    return stopPolling;
  }, [build, appId, stopPolling]);

  const onTrigger = async (): Promise<void> => {
    if (!latestVersion) return;
    setError(null);
    setTriggering(true);
    try {
      // Stable idempotency key so double-clicks attach to the same job.
      const idempotencyKey = `dash:${latestVersion.id}`;
      const result = await buildsApi.trigger(
        appId,
        { appVersionId: latestVersion.id, idempotencyKey },
        idempotencyKey,
      );
      setBuild(result);
    } catch (err) {
      if (err instanceof ApiClientError) {
        if (err.isQuotaExceeded) {
          setError(err.message);
        } else if (err.status === 422) {
          const blocking = extractBlocking(err.details);
          setError(`Preflight failed${blocking ? `: ${blocking}` : ''}.`);
        } else {
          setError(err.message);
        }
      } else {
        setError('Failed to trigger build');
      }
    } finally {
      setTriggering(false);
    }
  };

  const onCancel = async (): Promise<void> => {
    if (!build) return;
    try {
      const next = await buildsApi.cancel(build.id);
      setBuild(next);
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message);
    }
  };

  const live = build ? isLive(build.status) : false;
  const signingReady =
    signing != null &&
    (signing.signingMode === 'auto' ||
      (signing.signingMode === 'upload' && signing.upload != null));

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <Link to={`/apps/${appId}`} className="muted">
            ← Editor
          </Link>
          <h1>Build</h1>
        </div>
      </div>

      {error && <Banner>{error}</Banner>}

      <SigningCard
        appId={appId}
        signing={signing}
        onChange={setSigning}
        setError={setError}
        locked={live}
      />

      <Card
        title="Latest version"
        description="The saved config that will be built. Edit & save in the editor to change it."
      >
        {latestVersion ? (
          <div className="build-trigger">
            <dl className="meta">
              <dt>Version</dt>
              <dd>{latestVersion.versionName}</dd>
              <dt>Version code</dt>
              <dd>{latestVersion.androidVersionCode}</dd>
            </dl>
            <Button onClick={onTrigger} loading={triggering} disabled={live || !signingReady}>
              {live ? 'Build in progress…' : 'Start build'}
            </Button>
          </div>
        ) : (
          <p className="muted">
            No saved version yet. <Link to={`/apps/${appId}`}>Edit & save the config</Link> first.
          </p>
        )}
        {latestVersion && !signingReady && (
          <p className="field-hint">Choose how this app is signed above before building.</p>
        )}
      </Card>

      <Card
        title="Build progress"
        description="Watch your build move through each stage. Download links appear when it succeeds."
        actions={build ? <StatusBadge status={build.status} kind="build" /> : undefined}
      >
        <BuildStages build={build} />
        {build && <BuildDetail build={build} onCancel={live ? onCancel : undefined} />}
      </Card>

      {history.length > 0 && (
        <Card title="Build history">
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Created</th>
                <th>Artifacts</th>
              </tr>
            </thead>
            <tbody>
              {history.map((b) => (
                <tr key={b.id}>
                  <td>
                    <StatusBadge status={b.status} kind="build" />
                  </td>
                  <td className="muted">{new Date(b.createdAt).toLocaleString()}</td>
                  <td>
                    {b.status === 'succeeded' ? (
                      <ArtifactLinks buildId={b.id} />
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}

// ─── Build-stage stepper — shows the full pipeline + where this build is ─────

const STAGES: { key: BuildStatus; label: string; desc: string }[] = [
  { key: 'queued', label: 'Queued', desc: 'Waiting for a build node to pick it up' },
  { key: 'claimed', label: 'Claimed', desc: 'A build node has taken the job' },
  { key: 'building', label: 'Building', desc: 'Compiling the project & signing the app' },
  { key: 'uploading', label: 'Uploading', desc: 'Storing the signed AAB / APK' },
  { key: 'succeeded', label: 'Done', desc: 'Artifacts are ready to download' },
];
const STAGE_ORDER: BuildStatus[] = ['queued', 'claimed', 'building', 'uploading', 'succeeded'];

function BuildStages({ build }: { build: BuildDto | null }): JSX.Element {
  const status = build?.status ?? null;
  const failed = status === 'failed' || status === 'canceled';
  const currentIdx = status && !failed ? STAGE_ORDER.indexOf(status) : -1;

  return (
    <div className="build-stages" role="list" aria-label="Build stages">
      {STAGES.map((stage, i) => {
        let state: 'done' | 'active' | 'pending' = 'pending';
        if (!failed) {
          if (status === 'succeeded' || i < currentIdx) state = 'done';
          else if (i === currentIdx) state = 'active';
        }
        const isLast = i === STAGES.length - 1;
        return (
          <div
            key={stage.key}
            className={`stage stage-${state}${failed ? ' stage-dimmed' : ''}`}
            role="listitem"
          >
            <div className="stage-marker">
              <span className="stage-dot">
                {state === 'done' ? '✓' : state === 'active' ? '●' : i + 1}
              </span>
              {!isLast && <span className="stage-line" />}
            </div>
            <div className="stage-text">
              <span className="stage-label">{stage.label}</span>
              <span className="stage-desc muted">{stage.desc}</span>
            </div>
          </div>
        );
      })}

      {failed && (
        <div className="stage stage-failed" role="listitem">
          <div className="stage-marker">
            <span className="stage-dot">✕</span>
          </div>
          <div className="stage-text">
            <span className="stage-label">{status === 'canceled' ? 'Canceled' : 'Failed'}</span>
            <span className="stage-desc muted">
              {status === 'canceled'
                ? 'The build was canceled before finishing'
                : 'The build stopped before finishing — see the error below'}
            </span>
          </div>
        </div>
      )}

      {!build && (
        <p className="muted stage-idle-note">
          No build yet — start one above to watch it move through these stages live.
        </p>
      )}
    </div>
  );
}

function BuildDetail({
  build,
  onCancel,
}: {
  build: BuildDto;
  onCancel?: () => void;
}): JSX.Element {
  const live = isLive(build.status);
  return (
    <div>
      {live && build.estimatedWaitSeconds !== undefined && (
        <p>
          <span className="eta-pill">
            ⏱{' '}
            {build.status === 'queued' && build.queuePosition
              ? `Position ${build.queuePosition} in queue · ~${etaText(build.estimatedWaitSeconds)} wait`
              : `~${etaText(build.estimatedWaitSeconds)} remaining`}
          </span>
        </p>
      )}

      {build.status === 'failed' && build.error && <Banner>{build.error}</Banner>}

      {build.status === 'succeeded' && (
        <>
          <p className="success-text">Build complete. Download your app:</p>
          <ArtifactLinks buildId={build.id} />
        </>
      )}

      {build.logUrl && (
        <p>
          <a href={build.logUrl} target="_blank" rel="noreferrer">
            View build log
          </a>
        </p>
      )}

      {onCancel && (
        <Button variant="danger" className="btn-sm" onClick={onCancel}>
          Cancel build
        </Button>
      )}
    </div>
  );
}

function ArtifactLinks({ buildId }: { buildId: string }): JSX.Element {
  const [busy, setBusy] = useState<'apk' | 'aab' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [cert, setCert] = useState<string | null>(null);
  const download = async (type: 'apk' | 'aab'): Promise<void> => {
    setBusy(type);
    setErr(null);
    try {
      const res = await buildsApi.artifact(buildId, type);
      if (res.certSha256) setCert(res.certSha256);
      window.location.href = res.downloadUrl; // navigate to the signed build-VPS URL → download
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : 'Download failed');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="artifact-links">
      <Button className="btn-sm" loading={busy === 'aab'} onClick={() => void download('aab')}>
        Download for Play (.aab)
      </Button>
      <Button variant="secondary" className="btn-sm" loading={busy === 'apk'} onClick={() => void download('apk')}>
        Test APK
      </Button>
      {err && <span className="field-error">{err}</span>}
      {cert && (
        <p className="field-hint" style={{ flexBasis: '100%', margin: 0 }}>
          🔐 Upload-signing certificate (register in Play Console → App integrity):{' '}
          <span className="mono">{cert}</span>
        </p>
      )}
    </div>
  );
}

// ─── Signing: choose keystore source, upload BYO keystore ────────────────────

function SigningCard({
  appId,
  signing,
  onChange,
  setError,
  locked,
}: {
  appId: string;
  signing: SigningStatusDto | null;
  onChange: (s: SigningStatusDto) => void;
  setError: (e: string | null) => void;
  locked: boolean;
}): JSX.Element {
  const [busy, setBusy] = useState(false);

  if (!signing) {
    return (
      <Card
      title="App signing"
      description="How your app is signed for Google Play. Choose auto-generate (recommended) or upload your own keystore."
    >
        <p className="muted">Loading…</p>
      </Card>
    );
  }
  const mode = signing.signingMode;

  const pick = async (next: SigningMode): Promise<void> => {
    if (locked || busy || mode === next) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await signingApi.setMode(appId, next));
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to set signing mode');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="App signing"
      description="How your app is signed for Google Play. Choose auto-generate (recommended) or upload your own keystore."
    >
      {mode == null && (
        <Banner tone="info">
          Choose how this app’s Android signing keystore is handled — required before the first build.
        </Banner>
      )}
      <div className="signing-options">
        <SigningOption
          selected={mode === 'auto'}
          disabled={locked || busy}
          onSelect={() => void pick('auto')}
          title="Generate it for me"
          desc="App Builder creates a per-app upload keystore, signs your builds, and keeps an encrypted backup. Recommended — pair with Play App Signing so a lost key is resettable."
        />
        <SigningOption
          selected={mode === 'upload'}
          disabled={locked || busy}
          onSelect={() => void pick('upload')}
          title="Upload my own keystore"
          desc="Bring an existing .jks / .p12 keystore and its passwords. Stored encrypted and used only to sign your builds."
        />
      </div>
      {mode === 'upload' && (
        <KeystoreUpload
          appId={appId}
          upload={signing.upload}
          locked={locked}
          onChange={onChange}
          setError={setError}
        />
      )}
    </Card>
  );
}

function SigningOption({
  selected,
  disabled,
  onSelect,
  title,
  desc,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  title: string;
  desc: string;
}): JSX.Element {
  return (
    <button
      type="button"
      className={`signing-option${selected ? ' selected' : ''}`}
      disabled={disabled}
      onClick={onSelect}
    >
      <span className="signing-option-radio" aria-hidden>
        {selected ? '◉' : '◯'}
      </span>
      <span className="signing-option-text">
        <span className="signing-option-title">{title}</span>
        <span className="signing-option-desc muted">{desc}</span>
      </span>
    </button>
  );
}

function KeystoreUpload({
  appId,
  upload,
  locked,
  onChange,
  setError,
}: {
  appId: string;
  upload: SigningStatusDto['upload'];
  locked: boolean;
  onChange: (s: SigningStatusDto) => void;
  setError: (e: string | null) => void;
}): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [storePassword, setStorePassword] = useState('');
  const [keyAlias, setKeyAlias] = useState('');
  const [keyPassword, setKeyPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [replacing, setReplacing] = useState(false);

  const hasUpload = upload != null;
  const showForm = !hasUpload || replacing;

  const submit = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) {
      setError('Select your keystore file');
      return;
    }
    if (!storePassword) {
      setError('Store password is required');
      return;
    }
    if (!keyAlias) {
      setError('Key alias is required');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await signingApi.uploadKeystore(appId, file, {
        storePassword,
        keyAlias,
        keyPassword: keyPassword || undefined,
      });
      onChange(next);
      setReplacing(false);
      setFile(null);
      setStorePassword('');
      setKeyAlias('');
      setKeyPassword('');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Failed to upload keystore');
    } finally {
      setBusy(false);
    }
  };

  const statusTone =
    upload?.status === 'validated' ? 'badge-success'
    : upload?.status === 'rejected' ? 'badge-danger'
    : 'badge-warn';

  return (
    <div className="keystore-upload">
      {hasUpload && (
        <div className="keystore-current">
          <div>
            <strong>{upload!.originalFilename}</strong>{' '}
            <span className={`badge ${statusTone}`}>
              {upload!.status === 'pending_validation' ? 'Pending validation' : upload!.status}
            </span>
            <p className="muted">
              Uploaded {new Date(upload!.uploadedAt).toLocaleString()}. The keystore is validated on
              the build server when you build.
            </p>
            {upload!.validationError && <Banner>{upload!.validationError}</Banner>}
          </div>
          {!locked && !replacing && (
            <Button variant="secondary" className="btn-sm" onClick={() => setReplacing(true)}>
              Replace
            </Button>
          )}
        </div>
      )}

      {showForm && (
        <form className="form" onSubmit={submit}>
          <div className="field">
            <label className="field-label">Keystore file (.jks / .p12)</label>
            <input
              className="field-input"
              type="file"
              accept=".jks,.keystore,.p12,.pfx"
              disabled={locked || busy}
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          </div>
          <Field
            label="Store password"
            type="password"
            value={storePassword}
            onChange={(e) => setStorePassword(e.target.value)}
            disabled={locked || busy}
            required
          />
          <Field
            label="Key alias"
            value={keyAlias}
            onChange={(e) => setKeyAlias(e.target.value)}
            className="mono"
            disabled={locked || busy}
            required
          />
          <Field
            label="Key password"
            hint="Leave blank if it's the same as the store password"
            type="password"
            value={keyPassword}
            onChange={(e) => setKeyPassword(e.target.value)}
            disabled={locked || busy}
          />
          <div className="editor-save">
            <Button type="submit" loading={busy} disabled={locked}>
              {hasUpload ? 'Replace keystore' : 'Upload keystore'}
            </Button>
            {replacing && (
              <Button variant="ghost" onClick={() => setReplacing(false)} disabled={busy}>
                Cancel
              </Button>
            )}
          </div>
          <p className="field-hint">
            Your keystore and passwords are encrypted on upload and are <strong>write-only</strong> —
            we can’t display them back. They’re decrypted only on the build server to sign your app.
          </p>
        </form>
      )}
    </div>
  );
}

/** Human ETA: seconds → "45s" or "~3 min". */
function etaText(sec: number): string {
  return sec < 60 ? `${Math.max(1, Math.round(sec))}s` : `${Math.ceil(sec / 60)} min`;
}

/** Pull blocking-check ids out of a 422 preflight error envelope, best-effort. */
function extractBlocking(details: unknown): string | null {
  if (details && typeof details === 'object' && 'blocking' in details) {
    const blocking = (details as { blocking?: unknown }).blocking;
    if (Array.isArray(blocking)) return blocking.join(', ');
  }
  return null;
}
